<template>
  <div v-if="isImpersonating" class="impersonation-banner">
    <div class="banner-content">
      <el-icon class="banner-icon"><Warning /></el-icon>
      <span class="banner-text">
        <strong>{{ targetUserName }}</strong> として代理ログイン中
        <span class="expiry-info">
          (残り時間: {{ formatRemainingTime(expiresAt) }})
        </span>
      </span>
      <el-button 
        type="danger" 
        size="small" 
        @click="stopImpersonation"
        :loading="isLoading"
        class="stop-button"
      >
        代理ログイン終了
      </el-button>
    </div>
  </div>
</template>

<script>
import { ref, computed, inject, onMounted, onUnmounted } from 'vue'
import { Warning } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { differenceInMinutes, differenceInHours, isPast } from 'date-fns'
import axios from 'axios'

export default {
  name: 'ImpersonationBanner',
  components: {
    Warning
  },
  setup() {
    const auth = inject('Auth')
    const backendUrl = inject('BackendUrl')
    const isLoading = ref(false)
    const impersonationData = ref(null)
    const intervalId = ref(null)

    const isImpersonating = computed(() => {
      return !!impersonationData.value && !isExpired.value
    })

    const targetUserName = computed(() => {
      return impersonationData.value?.targetUser?.name || ''
    })

    const expiresAt = computed(() => {
      return impersonationData.value?.expiresAt || null
    })

    const isExpired = computed(() => {
      if (!expiresAt.value) return false
      return new Date() >= new Date(expiresAt.value)
    })

    // Check impersonation status
    const checkImpersonationStatus = async () => {
      try {
        const idToken = (await auth.getIdTokenClaims({}))?.__raw
        const impersonationToken = localStorage.getItem('impersonationToken')
        
        if (!impersonationToken) {
          impersonationData.value = null
          return
        }

        const response = await axios.get(`${backendUrl}/v1/admin/impersonation-status`, {
          headers: { 
            Authorization: `Bearer ${idToken}`,
            'X-AIMEAT-Impersonation-Token': impersonationToken
          }
        })

        if (response.data.isImpersonating) {
          impersonationData.value = response.data.session
        } else {
          impersonationData.value = null
          localStorage.removeItem('impersonationToken')
        }
      } catch (error) {
        console.error('Failed to check impersonation status:', error)
        impersonationData.value = null
        localStorage.removeItem('impersonationToken')
      }
    }

    // Stop impersonation
    const stopImpersonation = async () => {
      try {
        await ElMessageBox.confirm(
          '代理ログインを終了しますか？',
          '確認',
          {
            confirmButtonText: 'はい',
            cancelButtonText: 'キャンセル',
            type: 'warning',
          }
        )

        isLoading.value = true
        const idToken = (await auth.getIdTokenClaims({}))?.__raw
        await axios.delete(`${backendUrl}/v1/admin/impersonate`, {
          headers: { Authorization: `Bearer ${idToken}` }
        })

        localStorage.removeItem('impersonationToken')
        ElMessage.success('代理ログインを終了しました')
        
        // Refresh the page to remove impersonation context
        window.location.reload()
        
      } catch (error) {
        if (error.response) {
          ElMessage.error(error.response.data.error || '代理ログイン終了に失敗しました')
        } else if (error !== 'cancel') {
          ElMessage.error('代理ログイン終了に失敗しました')
        }
      } finally {
        isLoading.value = false
      }
    }

    // Format remaining time using date-fns
    const formatRemainingTime = (dateString) => {
      if (!dateString) return ''
      const expiry = new Date(dateString)
      const now = new Date()
      
      if (isPast(expiry)) return '期限切れ'
      
      const hours = differenceInHours(expiry, now)
      const minutes = differenceInMinutes(expiry, now) % 60
      
      if (hours > 0) {
        return `${hours}時間${minutes}分`
      } else {
        return `${minutes}分`
      }
    }

    // Check for expiration
    const checkExpiration = () => {
      if (isExpired.value) {
        localStorage.removeItem('impersonationToken')
        ElMessage.warning('代理ログインセッションが期限切れになりました')
        window.location.reload()
      }
    }

    onMounted(async () => {
      await checkImpersonationStatus()
      
      // Check status every 30 seconds
      intervalId.value = setInterval(() => {
        checkExpiration()
        checkImpersonationStatus()
      }, 30000)
    })

    onUnmounted(() => {
      if (intervalId.value) {
        clearInterval(intervalId.value)
      }
    })

    return {
      isImpersonating,
      targetUserName,
      expiresAt,
      isLoading,
      stopImpersonation,
      formatRemainingTime
    }
  }
}
</script>

<style scoped>
.impersonation-banner {
  background: linear-gradient(90deg, #f56c6c, #ff7875);
  color: white;
  padding: 8px 16px;
  box-shadow: 0 2px 8px rgba(245, 108, 108, 0.3);
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid #f56c6c;
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 1200px;
  margin: 0 auto;
  gap: 12px;
}

.banner-icon {
  font-size: 18px;
  color: #fff;
}

.banner-text {
  flex: 1;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
}

.expiry-info {
  font-weight: normal;
  opacity: 0.9;
}

.stop-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 12px;
  padding: 4px 12px;
}

.stop-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

@media print {
  .impersonation-banner {
    display: none !important;
  }
}

@media (max-width: 768px) {
  .banner-content {
    flex-direction: column;
    gap: 8px;
  }
  
  .banner-text {
    text-align: center;
  }
}
</style>
